/**
 * InterviewsTab - Onglet Entretiens
 * Gestion des entretiens (annuel, professionnel, etc.)
 */

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { ScrollArea } from "../../ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { 
  Plus, MessageSquare, Calendar, MapPin, User, Clock,
  Loader2, Trash2, Edit2, CheckCircle, FileText
} from "lucide-react";
import { DatePickerFr, formatDateFr } from "../../ui/date-picker-fr";
import {
  INTERVIEW_TYPE_OPTIONS,
  INTERVIEW_STATUS_OPTIONS,
  getInterviewTypeLabel,
  getInterviewStatusLabel,
  getInterviewStatusColor,
} from "../../../lib/hr-constants";

const API = process.env.REACT_APP_BACKEND_URL;

export default function InterviewsTab({ employee }) {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    interview_type: "",
    scheduled_date: "",
    scheduled_time: "",
    interviewer_name: "",
    location: "",
    notes: "",
  });
  
  const [editForm, setEditForm] = useState({});

  const token = localStorage.getItem("token");

  const fetchInterviews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/hr/employees/${employee.id}/interviews`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInterviews(response.data.interviews || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [employee.id, token]);

  useEffect(() => {
    fetchInterviews();
  }, [fetchInterviews]);

  const handleAdd = async () => {
    if (!form.interview_type || !form.scheduled_date) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/api/hr/employees/${employee.id}/interviews`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowAddDialog(false);
      setForm({ interview_type: "", scheduled_date: "", scheduled_time: "", interviewer_name: "", location: "", notes: "" });
      fetchInterviews();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedInterview) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/api/hr/employees/${employee.id}/interviews/${selectedInterview.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowEditDialog(false);
      fetchInterviews();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedInterview) return;
    setSaving(true);
    try {
      await axios.delete(
        `${API}/api/hr/employees/${employee.id}/interviews/${selectedInterview.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowDeleteDialog(false);
      fetchInterviews();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (i) => {
    setSelectedInterview(i);
    setEditForm({
      interview_type: i.interview_type || "",
      scheduled_date: i.scheduled_date || "",
      scheduled_time: i.scheduled_time || "",
      interviewer_name: i.interviewer_name || "",
      location: i.location || "",
      status: i.status || "",
      completed_date: i.completed_date || "",
      summary: i.summary || "",
      objectives: i.objectives || "",
      employee_comments: i.employee_comments || "",
      manager_comments: i.manager_comments || "",
      next_interview_date: i.next_interview_date || "",
      notes: i.notes || "",
    });
    setShowEditDialog(true);
  };

  const upcoming = interviews.filter(i => i.status === "scheduled" || i.status === "postponed");
  const past = interviews.filter(i => i.status === "completed" || i.status === "cancelled");

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-slate-900">Entretiens</h3>
          <Badge variant="secondary">{interviews.length}</Badge>
        </div>
        <Button onClick={() => setShowAddDialog(true)} data-testid="add-interview-btn">
          <Plus className="w-4 h-4 mr-2" />
          Planifier un entretien
        </Button>
      </div>

      {upcoming.length > 0 && (
        <Card className="border-purple-200 bg-purple-50/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-purple-600" />
              À venir ({upcoming.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {upcoming.map(i => (
              <InterviewCard key={i.id} interview={i} onEdit={() => openEdit(i)} onDelete={() => { setSelectedInterview(i); setShowDeleteDialog(true); }} />
            ))}
          </CardContent>
        </Card>
      )}

      {past.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              Historique ({past.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <ScrollArea className="max-h-[300px]">
              <div className="space-y-3">
                {past.map(i => (
                  <InterviewCard key={i.id} interview={i} onEdit={() => openEdit(i)} onDelete={() => { setSelectedInterview(i); setShowDeleteDialog(true); }} />
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {interviews.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">Aucun entretien</h3>
            <p className="text-sm text-slate-500 mb-4">Planifiez un entretien pour cet employé</p>
            <Button onClick={() => setShowAddDialog(true)}><Plus className="w-4 h-4 mr-2" />Planifier</Button>
          </CardContent>
        </Card>
      )}

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Planifier un entretien</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type d'entretien *</Label>
              <Select value={form.interview_type} onValueChange={(v) => setForm({...form, interview_type: v})}>
                <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                <SelectContent>
                  {INTERVIEW_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <DatePickerFr value={form.scheduled_date} onChange={(v) => setForm({...form, scheduled_date: v})} />
              </div>
              <div className="space-y-2">
                <Label>Heure</Label>
                <Input type="time" value={form.scheduled_time} onChange={(e) => setForm({...form, scheduled_time: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Interviewer</Label>
              <Input value={form.interviewer_name} onChange={(e) => setForm({...form, interviewer_name: e.target.value})} placeholder="Nom du manager / RH" />
            </div>
            <div className="space-y-2">
              <Label>Lieu</Label>
              <Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} placeholder="Salle, visio..." />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
            <Button onClick={handleAdd} disabled={saving || !form.interview_type || !form.scheduled_date}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Planifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifier l'entretien</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={editForm.interview_type} onValueChange={(v) => setEditForm({...editForm, interview_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INTERVIEW_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INTERVIEW_STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date prévue</Label>
                <DatePickerFr value={editForm.scheduled_date} onChange={(v) => setEditForm({...editForm, scheduled_date: v})} />
              </div>
              <div className="space-y-2">
                <Label>Date réalisation</Label>
                <DatePickerFr value={editForm.completed_date} onChange={(v) => setEditForm({...editForm, completed_date: v})} />
              </div>
            </div>
            <hr />
            <h4 className="font-medium">Compte-rendu</h4>
            <div className="space-y-2">
              <Label>Résumé</Label>
              <Textarea value={editForm.summary} onChange={(e) => setEditForm({...editForm, summary: e.target.value})} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Objectifs fixés</Label>
              <Textarea value={editForm.objectives} onChange={(e) => setEditForm({...editForm, objectives: e.target.value})} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Commentaires employé</Label>
              <Textarea value={editForm.employee_comments} onChange={(e) => setEditForm({...editForm, employee_comments: e.target.value})} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Commentaires manager</Label>
              <Textarea value={editForm.manager_comments} onChange={(e) => setEditForm({...editForm, manager_comments: e.target.value})} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Prochain entretien prévu</Label>
              <DatePickerFr value={editForm.next_interview_date} onChange={(v) => setEditForm({...editForm, next_interview_date: v})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Annuler</Button>
            <Button onClick={handleEdit} disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet entretien ?</AlertDialogTitle>
            <AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function InterviewCard({ interview, onEdit, onDelete }) {
  return (
    <div className="flex items-start gap-4 p-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors group">
      <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center shrink-0">
        <MessageSquare className="w-5 h-5 text-purple-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className="font-medium text-slate-900">{getInterviewTypeLabel(interview.interview_type)}</span>
          <Badge variant="outline" className={getInterviewStatusColor(interview.status)}>{getInterviewStatusLabel(interview.status)}</Badge>
        </div>
        <div className="flex flex-wrap gap-x-4 text-sm text-slate-500">
          <span className="flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {formatDateFr(interview.scheduled_date)}
            {interview.scheduled_time && ` à ${interview.scheduled_time}`}
          </span>
          {interview.interviewer_name && <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{interview.interviewer_name}</span>}
          {interview.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{interview.location}</span>}
        </div>
        {interview.summary && <p className="text-sm text-slate-600 mt-2 line-clamp-2">{interview.summary}</p>}
        {interview.next_interview_date && <p className="text-xs text-slate-400 mt-1">Prochain entretien : {formatDateFr(interview.next_interview_date)}</p>}
      </div>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onEdit}><Edit2 className="w-4 h-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={onDelete}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
